// Library Methods
import { useTranslation } from "react-i18next";
import { useCallback } from "react";

// Components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";

import DefaultCard from "src/components/shared/layouts/DefaultCard/DefaultCard";

// Styles
import { getFeatureRowLabelColor } from "src/styles/mui/muiStylesHelper";

const ContrastSensitivity = () => {
  const { t } = useTranslation("operating-manual");
  const theme = useTheme();
  const rowLabelColor = getFeatureRowLabelColor(theme);

  const tableContent = [
    {
      title: t("Pelli Robson"),
      content: t(
        "A standardized test to measure how well patients can distinguish objects from backgrounds of a similar shade. The letters stay the same size but gradually fade in contrast, and the faintest letters the patient can read indicate their contrast sensitivity"
      ),
    },
    {
      title: t("Tumbling E"),
      content: t(
        "Uses only the letter 'E', which appears in different sizes and rotated directions. Instead of reading letters, patients simply state which way the E points (up, down, left, or right)"
      ),
    },
  ];

  const getTableRow = useCallback(
    (title: string, content: string) => (
      <Grid container key={title}>
        <Grid
          item
          xs={6}
          sm={3}
          sx={{ display: "flex", flexDirection: "column", gap: "20px" }}
        >
          <Typography color={rowLabelColor} fontSize="14px" fontWeight={600}>
            {t(title)}
          </Typography>
        </Grid>
        <Grid
          item
          xs={6}
          sm={9}
          sx={{ display: "flex", flexDirection: "column", gap: "20px" }}
        >
          <Typography fontSize="14px">{t(content)}</Typography>
        </Grid>
      </Grid>
    ),
    [t, rowLabelColor]
  );

  return (
    <DefaultCard>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        <Grid container>
          <Grid
            item
            xs={6}
            sm={3}
            sx={{ display: "flex", flexDirection: "column", gap: "20px" }}
          >
            <Typography fontWeight={600}>{t("Features")}</Typography>
          </Grid>
          <Grid
            item
            xs={6}
            sm={9}
            sx={{ display: "flex", flexDirection: "column", gap: "20px" }}
          >
            <Typography fontWeight={600}>{t("Description")}</Typography>
          </Grid>
        </Grid>

        {tableContent.map((tableItem) =>
          getTableRow(tableItem.title, tableItem.content)
        )}
      </Box>
    </DefaultCard>
  );
};

export default ContrastSensitivity;
